import { CalculationFunctionBase } from "./AutoCalculations/CalculationFunctionBase";
import { CalculationFunctionDependency } from "./CalculationFunctionDependency";
import { CalculationTarget, CalculationTargetAttribute } from "./CalculationTarget";
import { IEvaluationContext } from "./IEvaluationContext";
import { LegacyValidationFunctionAdapter } from "./Validations/LegacyValidationFunctionAdapter";
import { ValidationFunction } from "./Validations/ValidationFunction";

interface ValidationFiunctionDebugInfo {
    valueValidators: string[];
    validationFunctions: string[];
}

export class AggregatedValidationFunction extends CalculationFunctionBase {
    private readonly validationFunctions: Array<ValidationFunction | LegacyValidationFunctionAdapter> = [];
    private readonly target: CalculationTarget;
    private dependencies?: readonly CalculationFunctionDependency[];

    public constructor(target: CalculationTarget) {
        super(target.modelPath);
        this.target = target;
    }

    public asDebugString(): string {
        return "[" + this.validationFunctions.map(x => x.asDebugString()).join(", ") + "]";
    }

    public addValidationFunction(validationFunction: ValidationFunction | LegacyValidationFunctionAdapter): void {
        this.validationFunctions.push(validationFunction);
        this.dependencies = undefined;
    }

    public async execute(context: IEvaluationContext): Promise<void> {
        const validationFunctions = this.validationFunctions;
        for (const validationFunction of validationFunctions) {
            await validationFunction.execute(context);
        }
    }

    public getDependencies(): readonly CalculationFunctionDependency[] {
        return this.dependencies ?? (this.dependencies = this.buildDependencies());
    }

    public getTarget(): CalculationTarget {
        return this.target;
    }

    public getValidationFunctionDebugInfo(): ValidationFiunctionDebugInfo {
        const result: ValidationFiunctionDebugInfo = { valueValidators: [], validationFunctions: [] };
        for (const validationFunction of this.validationFunctions) {
            if (validationFunction instanceof LegacyValidationFunctionAdapter) {
                result.valueValidators.push(validationFunction.asDebugString());
            }
            if (validationFunction instanceof ValidationFunction) {
                result.validationFunctions.push(validationFunction.asDebugString());
            }
        }
        return result;
    }

    public isDependsOn(modelPath: CalculationTarget): boolean {
        return (
            modelPath.targetAttribute === CalculationTargetAttribute.Value &&
            this.getDependencies().some(x => x.isMatchToPath(modelPath.modelPath))
        );
    }

    private buildDependencies() {
        const result: Array<readonly CalculationFunctionDependency[]> = [];
        for (const validationFunction of this.validationFunctions) {
            result.push(validationFunction.getDependencies());
        }
        return result.flat();
    }
}
